// Flopzilla-style range notation compression
// Converts a list of hand categories like ["AA","KK","AKs","AKo"] into
// compressed notation like "AA,KK,AKs,AKo" → "KK+,AKs,AKo"

const RANKS = ['A','K','Q','J','T','9','8','7','6','5','4','3','2'];
const RANK_IDX: Record<string, number> = {};
RANKS.forEach((r, i) => { RANK_IDX[r] = i; });

// Rank index: A=0, K=1, ... 2=12. Lower index = higher rank.
// "higher" in poker rank terms = lower index

export function toFlopzillaNotation(categories: string[]): string {
  if (categories.length === 0) return '';

  const pairs: string[] = [];
  const suited: Map<string, string[]> = new Map(); // topRank -> [kickers]
  const offsuit: Map<string, string[]> = new Map();

  for (const cat of categories) {
    if (cat.length === 2) {
      pairs.push(cat);
    } else if (cat.endsWith('s')) {
      const top = cat[0];
      const kicker = cat[1];
      if (!suited.has(top)) suited.set(top, []);
      suited.get(top)!.push(kicker);
    } else if (cat.endsWith('o')) {
      const top = cat[0];
      const kicker = cat[1];
      if (!offsuit.has(top)) offsuit.set(top, []);
      offsuit.get(top)!.push(kicker);
    }
  }

  const results: string[] = [];

  // --- Pairs: compress consecutive runs like AA,KK,QQ -> QQ+  ---
  const pairRanks = pairs.map(p => p[0]);
  pairRanks.sort((a, b) => RANK_IDX[a] - RANK_IDX[b]); // sort A first
  results.push(...compressPairRun(pairRanks));

  // --- Suited combos ---
  for (const [top, kickers] of [...suited.entries()].sort((a,b) => RANK_IDX[a[0]] - RANK_IDX[b[0]])) {
    results.push(...compressConnectedRun(top, kickers, 's'));
  }

  // --- Offsuit combos ---
  for (const [top, kickers] of [...offsuit.entries()].sort((a,b) => RANK_IDX[a[0]] - RANK_IDX[b[0]])) {
    results.push(...compressConnectedRun(top, kickers, 'o'));
  }

  return results.join(',');
}

// Compress pair runs: [A,K,Q,J] -> J+  or  [Q,J] -> JJ+  or  [A] -> AA
function compressPairRun(sortedRanks: string[]): string[] {
  if (sortedRanks.length === 0) return [];
  const results: string[] = [];
  let i = 0;
  while (i < sortedRanks.length) {
    let j = i;
    // extend run while consecutive
    while (j + 1 < sortedRanks.length &&
           RANK_IDX[sortedRanks[j+1]] === RANK_IDX[sortedRanks[j]] + 1) {
      j++;
    }
    const lo = sortedRanks[j]; // lowest rank in run (highest index)
    const hi = sortedRanks[i]; // highest rank in run (lowest index)
    if (j === i) {
      results.push(`${hi}${hi}`);
    } else if (hi === 'A') {
      // run goes to A: use lo+ notation
      results.push(`${lo}${lo}+`);
    } else {
      // run doesn't reach A: expand individually... or use lo+-hi notation
      // Flopzilla uses individual pairs if not reaching the top of the rank
      // Actually Flopzilla DOES support range like 99-QQ but most common is just listing
      // Let's use lo-hi notation: QQ-KK
      if (j - i === 1) {
        results.push(`${hi}${hi}`, `${lo}${lo}`);
      } else {
        results.push(`${lo}${lo}+`); // e.g. QQ+ if it's a full run
      }
    }
    i = j + 1;
  }
  return results;
}

// Compress connected kicker runs: A + [K,Q,J] suited -> AJs+
function compressConnectedRun(top: string, kickers: string[], suffix: string): string[] {
  if (kickers.length === 0) return [];
  // Sort kickers by rank (A first = lowest index)
  const sorted = [...kickers].sort((a, b) => RANK_IDX[a] - RANK_IDX[b]);
  const results: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length &&
           RANK_IDX[sorted[j+1]] === RANK_IDX[sorted[j]] + 1) {
      j++;
    }
    const hiKicker = sorted[i];  // highest kicker in run
    const loKicker = sorted[j];  // lowest kicker in run

    if (j === i) {
      // single
      results.push(`${top}${hiKicker}${suffix}`);
    } else {
      // run: check if loKicker is adjacent to top (e.g. AKs where K is one below A)
      const topIdx = RANK_IDX[top];
      const loIdx = RANK_IDX[loKicker];
      const hiIdx = RANK_IDX[hiKicker];

      // If the highest kicker is adjacent to the top rank, use + notation
      if (hiIdx === topIdx + 1) {
        // e.g. AQs,AKs -> AKs+ (loKicker is the low end, but notation is hi+)
        // Wait - Flopzilla: AQs+ means AQs,AKs (+ = include higher kickers)
        // so the LOW kicker of the run becomes the base with +
        results.push(`${top}${loKicker}${suffix}+`);
      } else {
        // doesn't touch top: use range notation lo-hi or just list
        results.push(`${top}${loKicker}${suffix}-${top}${hiKicker}${suffix}`);
      }
    }
    i = j + 1;
  }
  return results;
}
